import { HelpCircle } from 'lucide-react';
import * as React from 'react';

import { Label } from '@apps/web/components/ui/label';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

/**
 * Shared building blocks for the settings surface, styled after Linear's
 * settings: a page is a stack of {@link SettingsSection} groups, each an
 * optionally-labeled, bordered/rounded card whose {@link SettingsRow} children
 * are separated by hairline dividers.
 *
 * Prefer these over hand-rolling `<Card>` + `flex justify-between` rows so every
 * settings page shares one type scale, one row rhythm, and one divider style.
 */

export type SettingsSectionProps = {
    /**
     * Section heading rendered above the card (Linear's "General" style label).
     * Omit on single-group pages where it would just repeat the page title.
     */
    readonly title?: string;
    readonly description?: string;
    readonly children: React.ReactNode;
    readonly className?: string;
    readonly variant?: 'card' | 'flat';
};

export function SettingsSection({
    title,
    description,
    children,
    className,
    variant = 'card',
}: SettingsSectionProps): React.JSX.Element {
    const hasHeading = title !== undefined || description !== undefined;
    const innerClassName =
        variant === 'flat'
            ? 'divide-border divide-y'
            : 'bg-card divide-border divide-y overflow-hidden rounded-lg border shadow-sm';

    return (
        <section className={cn('space-y-3', className)}>
            {hasHeading ? (
                <div className="space-y-1 px-1">
                    {title !== undefined ? (
                        <h2 className="text-foreground text-base font-semibold">
                            {title}
                        </h2>
                    ) : null}
                    {description !== undefined ? (
                        <p className="text-muted-foreground text-sm">
                            {description}
                        </p>
                    ) : null}
                </div>
            ) : null}
            <div className={innerClassName}>{children}</div>
        </section>
    );
}

export type SettingsPanelProps = {
    /** Section heading rendered above the card. Omit to repeat nothing. */
    readonly title?: string;
    readonly description?: string;
    readonly children: React.ReactNode;
    readonly className?: string;
    /** Overrides the default body padding (e.g. for edge-to-edge content). */
    readonly bodyClassName?: string;
};

/**
 * A bordered/rounded card for settings content that isn't a list of simple
 * rows — editors, forms, two-column layouts. Same chrome as
 * {@link SettingsSection} but a single padded body instead of divided rows.
 */
export function SettingsPanel({
    title,
    description,
    children,
    className,
    bodyClassName,
}: SettingsPanelProps): React.JSX.Element {
    const hasHeading = title !== undefined || description !== undefined;
    return (
        <section className={cn('space-y-3', className)}>
            {hasHeading ? (
                <div className="space-y-1 px-1">
                    {title !== undefined ? (
                        <h2 className="text-foreground text-base font-semibold">
                            {title}
                        </h2>
                    ) : null}
                    {description !== undefined ? (
                        <p className="text-muted-foreground text-sm">
                            {description}
                        </p>
                    ) : null}
                </div>
            ) : null}
            <div
                className={cn(
                    'bg-card overflow-hidden rounded-lg border shadow-sm',
                    bodyClassName ?? 'p-5',
                )}
            >
                {children}
            </div>
        </section>
    );
}

export type SettingsRowProps = {
    readonly title: React.ReactNode;
    readonly description?: React.ReactNode;
    /**
     * Help text shown behind a ? icon beside the title. The icon is a sibling
     * of the label, never a child — an interactive element inside a <label>
     * suppresses click-to-toggle and is a nested-interactive a11y violation.
     */
    readonly tooltip?: string;
    /** Binds the row label to a control for click-to-focus + a11y. */
    readonly htmlFor?: string;
    /**
     * An inline control (Switch / Select / small Input / Button) rendered
     * right-aligned and vertically centered against the label block.
     */
    readonly control?: React.ReactNode;
    /**
     * Content that stacks full-width beneath the label (textarea, list,
     * button row). Ignored when `control` is provided.
     */
    readonly children?: React.ReactNode;
    readonly className?: string;
};

/**
 * The standard settings "?" help tooltip: an icon button next to a label that
 * reveals explanatory copy on hover. Keep it outside the Label element so the
 * trigger button is never nested inside a label.
 */
export function SettingsHelpTip({
    children,
}: {
    readonly children: React.ReactNode;
}): React.JSX.Element {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    aria-label="More information"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                >
                    <HelpCircle className="h-4 w-4" />
                </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{children}</TooltipContent>
        </Tooltip>
    );
}

export function SettingsRow({
    title,
    description,
    tooltip,
    htmlFor,
    control,
    children,
    className,
}: SettingsRowProps): React.JSX.Element {
    const isInline = control !== undefined;
    const label = (
        <Label
            htmlFor={htmlFor}
            className="text-foreground text-[15px] font-medium"
        >
            {title}
        </Label>
    );
    return (
        <div className={cn('px-4 py-4 sm:px-5', className)}>
            <div
                className={cn(
                    'flex gap-4',
                    isInline
                        ? 'flex-col items-start sm:flex-row sm:items-center sm:justify-between'
                        : 'flex-col',
                )}
            >
                <div className="min-w-0 space-y-1">
                    {tooltip !== undefined ? (
                        <div className="flex items-center gap-2">
                            {label}
                            <SettingsHelpTip>{tooltip}</SettingsHelpTip>
                        </div>
                    ) : (
                        label
                    )}
                    {description !== undefined ? (
                        <div className="text-muted-foreground text-sm leading-snug">
                            {description}
                        </div>
                    ) : null}
                </div>
                {isInline ? (
                    <div className="w-full shrink-0 sm:w-auto">{control}</div>
                ) : null}
            </div>
            {!isInline && children !== undefined ? (
                <div className="mt-3">{children}</div>
            ) : null}
        </div>
    );
}
